import { TransferJobTaskStatus } from '@frameio/graphql-client/client-schema/transfers/state/transferJobTaskStatus';
import { createOrUpdateTransferState } from '@frameio/transfers/utils';
import { useCreateTransferAssets } from '../../shared/hooks/useCreateTransferAssets';
import { useDatadogLogger } from '../../shared/hooks/useDatadogLogger';
import { ensureFileNamesHaveExtensions } from '../../shared/utils/ensureFileNamesHaveExtensions';
import {
  type ExportRenderEventsSubscription,
  useExportRenderEventsSubscription,
} from '../gql';
import TEXT from './generated/TEXT/useRenderJobEvents';

TEXT.DEFINE({
  /** Toast title shown when an AE render fails because another render is already in progress */
  alreadyRenderingTitle: 'Another render is already in progress',
  /** Toast title shown when an AE render finishes without producing any output files (e.g., stopped midway) */
  noOutputTitle: 'Render stopped before producing any files',
  /** Toast title shown when an AE render fails because no After Effects project is open */
  noProjectTitle: 'No After Effects project open',
  /** Toast title shown when an AE render fails because the render queue is empty */
  nothingToRenderTitle: 'Nothing to render',
  /** Generic toast title shown when an AE render fails for an unknown reason */
  unknownErrorTitle: 'Render failed',
  /** Toast title shown when an AE render fails because the render output module is unsupported*/
  unsupportedRenderOutputTitle: 'Unsupported render output',
});

type RenderJobCompletePayload = Extract<
  ExportRenderEventsSubscription['exportRenderEvents'],
  { __typename?: 'RenderJobCompletePayload' }
>;

type RenderJobFailedPayload = Extract<
  ExportRenderEventsSubscription['exportRenderEvents'],
  { __typename?: 'RenderJobFailedPayload' }
>;

const failureTitleByReason: Record<RenderJobFailedPayload['reason'], string> = {
  get ALREADY_RENDERING() {
    return TEXT.alreadyRenderingTitle;
  },
  get NO_OUTPUT() {
    return TEXT.noOutputTitle;
  },
  get NO_PROJECT() {
    return TEXT.noProjectTitle;
  },
  get NOTHING_TO_RENDER() {
    return TEXT.nothingToRenderTitle;
  },
  get UNKNOWN() {
    return TEXT.unknownErrorTitle;
  },
  get UNSUPPORTED_RENDER_OUTPUT() {
    return TEXT.unsupportedRenderOutputTitle;
  },
};

type RenderJobEventsDeps = {
  getJobSettings: (jobId: string) =>
    | {
        folderId: string;
        projectId?: string;
        exportMarkersAsCommentsEnabled: boolean;
      }
    | undefined;
  registerRenderedAsset: (params: {
    assetId: string;
    jobId: string;
    fileName: string;
    folderId: string;
    compositionId: string;
    projectId?: string;
    workAreaDuration: number | null;
    workAreaStart: number | null;
    framesPerSecond?: number | null;
  }) => void;
  setRenderingCount: (count: number) => void;
  onRenderError: (message: string) => void;
};

/**
 * Subscribes to AE render-queue events and, on completion, creates transfer
 * assets in the user-selected folder (resolved from per-job settings) and
 * registers per-comp progress items so the UI can display them and the
 * upload-complete handler can persist the Frame.io ↔ composition link.
 * Takes its dependencies as arguments to avoid a cycle with the progress
 * context.
 */
export const useRenderJobEvents = ({
  getJobSettings,
  setRenderingCount,
  registerRenderedAsset,
  onRenderError,
}: RenderJobEventsDeps) => {
  const createTransferAssets = useCreateTransferAssets();
  const datadogLogger = useDatadogLogger();

  const handleRenderJobComplete = async (event: RenderJobCompletePayload) => {
    const { jobId, items } = event;
    const settings = getJobSettings(jobId);
    if (!settings) {
      // eslint-disable-next-line no-console
      console.error(
        `Missing job settings for AE render jobId=${jobId}; aborting upload.`
      );
      return;
    }

    const filePaths = items.map(({ filePath }) => filePath);
    const compositionIds = items.map(({ compositionId }) => compositionId);
    const workAreaStarts = items.map(
      ({ workAreaStart }) => workAreaStart ?? null
    );
    const workAreaDurations = items.map(
      ({ workAreaDuration }) => workAreaDuration ?? null
    );
    const framesPerSecondByItem = items.map(
      ({ framesPerSecond }) => framesPerSecond ?? null
    );
    const fileNames = ensureFileNamesHaveExtensions({
      fileNames: items.map(({ fileName }) => fileName),
      filePaths,
    });

    const result = await createTransferAssets({
      jobId,
      filePaths,
      fileNames,
      folderAssetId: settings.folderId,
    });

    result.assetIds?.forEach((assetId, index) => {
      registerRenderedAsset({
        assetId,
        jobId,
        fileName: fileNames[index],
        folderId: settings.folderId,
        compositionId: compositionIds[index],
        projectId: result.projectId,
        workAreaDuration: workAreaDurations[index],
        workAreaStart: workAreaStarts[index],
        framesPerSecond: framesPerSecondByItem[index],
      });
      createOrUpdateTransferState(assetId, {
        status: TransferJobTaskStatus.IDLE,
        assetId,
        jobId,
        filePath: filePaths[index],
      });
    });
  };

  useExportRenderEventsSubscription({
    context: { resolveWithUXP: true },
    onData: (subscriptionData) => {
      const event = subscriptionData.data?.data?.exportRenderEvents;
      if (!event) return;
      if (event.__typename === 'RenderJobStartedPayload') {
        setRenderingCount(event.totalItems);
        datadogLogger.info('integrations-encode-started', {
          uxpUploads: {
            source: 'AeUXP',
            item_count: event.totalItems,
          },
        });
      } else if (event.__typename === 'RenderJobCompletePayload') {
        setRenderingCount(0);
        datadogLogger.info('integrations-encode-completed', {
          uxpUploads: {
            source: 'AeUXP',
            transfer_job_id: event.jobId,
            item_count: event.items.length,
          },
        });
        handleRenderJobComplete(event);
      } else if (event.__typename === 'RenderJobFailedPayload') {
        setRenderingCount(0);
        datadogLogger.info('integrations-encode-failed', {
          uxpUploads: {
            source: 'AeUXP',
            reason: event.reason,
          },
        });
        onRenderError(failureTitleByReason[event.reason]);
      }
    },
  });
};
